import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridBaseQuery } from '@core';
import { InvestigationTypeEntity } from '../../domain/entities/investigation-type.entity';
import { InvestigationTypeDetailEntity } from '../../domain/entities/investigation-type-detail.entity';
import { InvestigationTypeCategoryEntity } from '../../domain/entities/investigation-type-category.entity';
import { InvestigationTypeDeletionImpactEntity } from '../../domain/entities/investigation-type-deletion-impact.entity';
import { ClosedCaseVisibility } from '../../domain/enums/closed-case-visibility.enum';
import { InvestigationTypeApi } from '../api/investigation-type.api';
import { InvestigationTypeMapper } from '../mappers/investigation-type.mapper';

export interface CreateInvestigationTypeParams {
  name: string;
  description: string;
  categoryId: string;
  isActive: boolean;
  sortOrder: number;
  closedCaseVisibility: ClosedCaseVisibility;
}

export interface UpdateInvestigationTypeParams {
  name: string;
  description: string;
  categoryId: string;
  isActive: boolean;
  sortOrder: number;
  closedCaseVisibility: ClosedCaseVisibility;
}

@Injectable({ providedIn: 'root' })
export class InvestigationTypeRepository {
  private readonly _api = inject(InvestigationTypeApi);
  private readonly _mapper = inject(InvestigationTypeMapper);

  public getAllInvestigationTypes(): Observable<InvestigationTypeEntity[]> {
    return this._api.getAllInvestigationTypes().pipe(map((result) => this._mapper.toDomainArray(result.data)));
  }

  public getActiveInvestigationTypes(): Observable<InvestigationTypeEntity[]> {
    return this._api.getActiveInvestigationTypes().pipe(map((dtos) => this._mapper.toDomainArray(dtos)));
  }

  public getInvestigationTypesWithPaging(query: GridBaseQuery): Observable<{ data: InvestigationTypeEntity[]; totalElements: number }> {
    return this._api.getInvestigationTypesWithPaging(query).pipe(
      map((result) => ({
        data: (result.data || []).map((dto) => this._mapper.toDomain(dto)),
        totalElements: result.totalElements || 0,
      })),
    );
  }

  public getInvestigationTypeById(id: string): Observable<InvestigationTypeDetailEntity> {
    return this._api.getInvestigationTypeById(id).pipe(map((dto) => this._mapper.toDetailDomain(dto)));
  }

  public getCategories(): Observable<InvestigationTypeCategoryEntity[]> {
    return this._api.getCategories().pipe(map((dtos) => this._mapper.toCategoryDomainArray(dtos)));
  }

  public createInvestigationType(params: CreateInvestigationTypeParams): Observable<{ id: string }> {
    return this._api.createInvestigationType({
      name: params.name,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
      sortOrder: params.sortOrder,
      closedCaseVisibility: params.closedCaseVisibility,
    }).pipe(map((dto) => ({ id: dto.id })));
  }

  public updateInvestigationType(id: string, params: UpdateInvestigationTypeParams): Observable<InvestigationTypeDetailEntity> {
    return this._api.updateInvestigationType(id, {
      name: params.name,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
      sortOrder: params.sortOrder,
      closedCaseVisibility: params.closedCaseVisibility,
    }).pipe(map((dto) => this._mapper.toDetailDomain(dto)));
  }

  public deleteInvestigationType(id: string): Observable<void> {
    return this._api.deleteInvestigationType(id);
  }

  public toggleInvestigationTypeActive(id: string, isActive: boolean): Observable<void> {
    return this._api.toggleInvestigationTypeActive(id, { isActive });
  }

  public getInvestigationTypeDeletionImpact(id: string): Observable<InvestigationTypeDeletionImpactEntity> {
    return this._api.getInvestigationTypeDeletionImpact(id).pipe(map((dto) => this._mapper.toDeletionImpactDomain(dto)));
  }
}
